import axios from 'axios';
import { MagritteNotificationName } from 'utils/constants/notification';

import videoDownloadEndFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/video_download_end_form_submit';
import videoDownloadStartFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_flexible/video_download_start_form_submit';

import { getThumbnail } from 'components/common/BannerVideo/getPreviewImage';
import { getUploadUrls } from 'components/common/BannerVideo/getUploadUrls';
import { uploadFiles } from 'components/common/BannerVideo/uploadFiles';
import { validateVideo } from 'components/common/BannerVideo/validateVideo';

import {
    resetBannerVideo,
    resetBannerVideoFiles,
    setBannerVideoFile,
    setBannerVideoHeight,
    setBannerVideoThumbnailFile,
    setBannerVideoThumbnailUploadId,
    setBannerVideoUploadId,
    setBannerVideoWidth,
    setIsVideoLoading,
} from 'store/models/createBanner';
import { addMagritteNotification } from 'store/models/notification';
import { resetSelectedVideo } from 'store/models/videoGallery';
import { AppThunkAction } from 'store/utils';

type LoadVideo = (props: {
    file: Blob;
    signal: AbortSignal;
    callback?: (isVideoLoading: boolean) => void;
    onProgress?: (percentage: number) => void;
}) => AppThunkAction;

const resetBannerVideoState = () => [
    resetBannerVideo(null),
    resetBannerVideoFiles(null),
    resetSelectedVideo(null),
    setIsVideoLoading(false),
];

export const loadVideo: LoadVideo = ({ file, signal, callback, onProgress }) => {
    return async (dispatch, getState) => {
        const hadInFlightUpload = getState().createBanner.isVideoLoading;

        try {
            callback?.(true);

            const { bannerVacancyId } = getState().createBanner;
            const vacancyId = bannerVacancyId ? String(bannerVacancyId) : null;

            const { isValidate, errors, meta } = await validateVideo(file);
            if (signal.aborted) {
                return;
            }
            if (!isValidate) {
                errors.forEach((error) => {
                    dispatch(addMagritteNotification(error));
                });
                if (hadInFlightUpload) {
                    dispatch(resetBannerVideoState());
                }
                return;
            }

            dispatch([resetBannerVideo(null), resetSelectedVideo(null), setBannerVideoFile(file)]);

            const { width, height } = meta;
            dispatch(setBannerVideoWidth(width));
            dispatch(setBannerVideoHeight(height));

            const [{ videoUrl, thumbnailUrl }, thumbnail] = await Promise.all([
                getUploadUrls(signal),
                getThumbnail(file),
            ]);
            if (signal.aborted) {
                return;
            }

            dispatch(setBannerVideoThumbnailFile(thumbnail));

            const thumbnailData = new FormData();
            thumbnailData.append('file', thumbnail);
            const videoData = new FormData();
            videoData.append('file', file);

            videoDownloadStartFormSubmit({
                vacancyId,
            });

            const { videoUploadId, thumbnailUploadId } = await uploadFiles({
                videoData,
                thumbnailData,
                videoUrl,
                thumbnailUrl,
                signal,
                onProgress,
            });
            if (signal.aborted) {
                return;
            }

            dispatch([setBannerVideoUploadId(videoUploadId), setBannerVideoThumbnailUploadId(thumbnailUploadId)]);

            videoDownloadEndFormSubmit({
                vacancyId,
            });
        } catch (error) {
            if (signal.aborted || axios.isCancel(error)) {
                return;
            }

            dispatch(resetBannerVideoState());
            if (axios.isAxiosError(error)) {
                dispatch(addMagritteNotification(MagritteNotificationName.CampaignFormVideoSomethingWentWrong));
            }
        } finally {
            callback?.(false);
        }
    };
};
